import { useEffect, useState } from 'react';
import { Card, DataTable, EmptyState, FormField, FormSelect, PageHeader } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Icon } from '@/components/ui/Icon';
import { PhoneCell } from '@/components/telephony/PhoneCell';
import { agentApi, type AgentLead } from '@/shared/api/agentApi';
import { useSipStore } from '../stores/sipStore';
import { toast } from '@/components/ui/Toast';

interface CampaignOption { id: number; name: string }

/** הסטטוסים שליד פתוח יכול להיות בהם — לפי מכונת המצבים של campaign_leads. */
const STATUS_OPTIONS: { value: string; he: string }[] = [
  { value: 'queued', he: 'בתור' },
  { value: 'calling', he: 'בחיוג' },
  { value: 'completed', he: 'הושלם' },
  { value: 'failed', he: 'נכשל' },
  { value: 'dnc', he: 'הסר מרשימה' },
];

const STATUS_HE: Record<string, string> = Object.fromEntries(
  STATUS_OPTIONS.map((s) => [s.value, s.he]),
);

/**
 * "בטיפולי" (אפיון 3.1) — הלידים הפתוחים של הנציג, עם סינון לפי קמפיין
 * וסטטוס. הרשימה מגיעה מ-GET /agent/leads; עד שהנקודה קיימת בשרת היא
 * מחזירה ריק וה-EmptyState מסביר את זה.
 */
export function MyLeads() {
  const [leads, setLeads] = useState<AgentLead[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);
  const [campaignId, setCampaignId] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const dial = useSipStore((s) => s.dial);
  const registered = useSipStore((s) => s.registered);

  useEffect(() => {
    void agentApi.dialerCampaigns()
      .then((d) => setCampaigns(d as CampaignOption[]))
      .catch(() => setCampaigns([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void agentApi.myLeads({
      campaign_id: campaignId ? Number(campaignId) : undefined,
      status: status || undefined,
    })
      .then((d) => { if (!cancelled) setLeads(d); })
      .catch(() => {
        if (cancelled) return;
        setLeads([]);
        toast('שגיאה בטעינת הלידים', 'danger');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [campaignId, status]);

  async function call(lead: AgentLead) {
    // בלי רישום SIP אין קו — dial() לא זורק, רק נכנס למצב שגיאה.
    if (!registered) {
      toast('הסופטפון לא מחובר — המתן לחיבור לפני חיוג', 'danger');
      return;
    }
    try {
      await dial(lead.phone);
    } catch {
      toast('שגיאה בחיוג', 'danger');
    }
  }

  const cols: DataTableColumn<AgentLead>[] = [
    { key: 'name', he: 'שם', render: (r) => r.name || '(ללא שם)' },
    { key: 'phone', he: 'טלפון', render: (r) => <PhoneCell number={r.phone} size="sm" /> },
    {
      key: 'campaign_name',
      he: 'קמפיין',
      render: (r) => r.campaign_name || campaigns.find((c) => c.id === r.campaign_id)?.name || '—',
    },
    { key: 'status', he: 'סטטוס', render: (r) => STATUS_HE[r.status] ?? r.status },
    {
      key: 'last_attempt_at',
      he: 'ניסיון אחרון',
      render: (r) => (r.last_attempt_at ? new Date(r.last_attempt_at).toLocaleString('he-IL') : '—'),
    },
    {
      key: 'id',
      he: '',
      render: (r) => (
        <button type="button" className="btn primary" onClick={() => void call(r)}>
          <Icon name="phone" size={14} /> חייג
        </button>
      ),
    },
  ];

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="בטיפולי" />

      <Card>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <FormField label="קמפיין">
            <FormSelect value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
              <option value="">כל הקמפיינים</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </FormSelect>
          </FormField>
          <FormField label="סטטוס">
            <FormSelect value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">כל הסטטוסים</option>
              {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.he}</option>)}
            </FormSelect>
          </FormField>
        </div>
      </Card>

      {!loading && leads.length === 0 ? (
        <EmptyState icon="target" title="אין לידים בטיפולך" description="לידים שיוקצו לך יופיעו כאן." />
      ) : (
        <Card padded={false}>
          <DataTable<AgentLead> columns={cols} rows={leads} loading={loading} />
        </Card>
      )}
    </div>
  );
}
